import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RefreshCcw, RotateCw, Search } from 'lucide-react';
import type { RobotSummary, RobotState } from '@fabricos/protocol-ts';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { sendCommand } from '@/hooks/useHubSocket';
import { useParams, paramGroup, type ParamValue } from '@/store/params';
import { useFleet } from '@/store/fleet';
import { paramMeta, type ParamMeta } from './paramMeta';
import { cn } from '@/lib/utils';

interface Props {
  robot: RobotSummary;
}

/**
 * Read-only ArduCopter parameter browser.
 *
 * Pipeline:
 *   1. Click "Load all" → frontend dispatches `Command::ParamReadAll`
 *      (new in this slice).
 *   2. Hub forwards `PARAM_REQUEST_LIST` to the autopilot.
 *   3. Autopilot streams `PARAM_VALUE` × N (≈1368 for Copter); Hub decodes
 *      each and emits `RobotEvent::ParamValue { id, value, type, index, count }`.
 *   4. WS dispatcher writes into `useParams` (this store).
 *   5. This browser renders progress + the grouped list.
 *
 * Write side per ADR 0014: armed guard (Hub-side), type-to-confirm dialog,
 * type passthrough, and READBACK VERIFICATION — after PARAM_SET the row
 * shows "verifying" until the autopilot echoes the new PARAM_VALUE
 * (→ verified) or 4 s pass (→ no-echo warning with re-read).
 */

type WriteStatus = 'verifying' | 'verified' | 'mismatch' | 'timeout';

interface PendingWrite {
  value: number;
  ts: number;
  status: WriteStatus;
}

const VERIFY_TIMEOUT_MS = 4000;
const VERIFIED_LINGER_MS = 6000;
export function ParamBrowser({ robot }: Props) {
  const params = useParams((s) => s.byRobot[robot.id] ?? {});
  const expected = useParams((s) => s.expected[robot.id] ?? 0);
  const clearRobot = useParams((s) => s.clearRobot);

  const [query, setQuery] = useState('');
  const [activeGroup, setActiveGroup] = useState<string>('all');
  const [editing, setEditing] = useState<ParamValue | null>(null);
  const [writes, setWrites] = useState<Record<string, PendingWrite>>({});

  // Readback verification (ADR 0014): resolve pending writes against the
  // autopilot's PARAM_VALUE echo, or flag silence after the timeout.
  useEffect(() => {
    setWrites((w) => {
      let changed = false;
      const next = { ...w };
      for (const [id, pw] of Object.entries(w)) {
        if (pw.status !== 'verifying') continue;
        const p = params[id];
        if (p && p.updated_at_ms >= pw.ts) {
          next[id] = {
            ...pw,
            status: Math.abs(p.value - pw.value) < 1e-6 ? 'verified' : 'mismatch',
          };
          changed = true;
        }
      }
      return changed ? next : w;
    });
  }, [params]);

  useEffect(() => {
    const t = window.setInterval(() => {
      setWrites((w) => {
        let changed = false;
        const now = Date.now();
        const next = { ...w };
        for (const [id, pw] of Object.entries(w)) {
          if (pw.status === 'verifying' && now - pw.ts > VERIFY_TIMEOUT_MS) {
            next[id] = { ...pw, status: 'timeout' };
            changed = true;
          } else if (pw.status === 'verified' && now - pw.ts > VERIFIED_LINGER_MS) {
            delete next[id];
            changed = true;
          }
        }
        return changed ? next : w;
      });
    }, 1000);
    return () => window.clearInterval(t);
  }, []);

  const list = useMemo(() => Object.values(params), [params]);
  const groups = useMemo(() => {
    const set = new Map<string, number>();
    for (const p of list) {
      const g = paramGroup(p.id);
      set.set(g, (set.get(g) ?? 0) + 1);
    }
    return Array.from(set.entries()).sort((a, b) => b[1] - a[1]);
  }, [list]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return list
      .filter((p) => activeGroup === 'all' || paramGroup(p.id) === activeGroup)
      .filter((p) => !q || p.id.toLowerCase().includes(q))
      .sort((a, b) => a.id.localeCompare(b.id));
  }, [list, query, activeGroup]);

  function loadAll() {
    sendCommand({
      kind: 'param_read_all',
      id: `param-load-${Date.now()}`,
      robot_id: robot.id,
      priority: 'normal',
    });
  }
  function refreshOne(p: ParamValue) {
    sendCommand({
      kind: 'param_read',
      id: `param-read-${Date.now()}`,
      robot_id: robot.id,
      priority: 'normal',
      param_id: p.id,
    });
  }

  const loadedCount = list.length;
  const progressPct = expected > 0 ? Math.min(100, Math.round((loadedCount / expected) * 100)) : 0;

  return (
    <div className="flex h-full flex-col">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 border-b border-border-default bg-surface-1 px-3 py-2">
        <Button size="sm" onClick={loadAll}>
          <RefreshCcw className="h-3 w-3" />
          {loadedCount === 0 ? 'Load all params' : 'Reload all'}
        </Button>
        <div className="font-mono text-[10px] text-fg-muted">
          {loadedCount} / {expected || '?'} loaded
          {expected > 0 && <span className="ml-2">({progressPct}%)</span>}
        </div>
        <div className="relative ml-auto">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-fg-muted/70" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="FILTER PARAM"
            className="h-7 w-44 pl-7 text-[11px] uppercase tracking-[0.1em]"
          />
        </div>
        <Button size="sm" variant="ghost" onClick={() => clearRobot(robot.id)} title="Clear cache">
          Clear
        </Button>
      </div>

      {/* Group pills */}
      {groups.length > 0 && (
        <div className="flex flex-wrap items-center gap-1 border-b border-border-subtle bg-surface-1 px-3 py-1.5">
          <GroupPill label="all" count={list.length} active={activeGroup === 'all'} onClick={() => setActiveGroup('all')} />
          {groups.map(([g, n]) => (
            <GroupPill key={g} label={g} count={n} active={activeGroup === g} onClick={() => setActiveGroup(g)} />
          ))}
        </div>
      )}

      {/* List */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden">
        {loadedCount === 0 ? (
          <EmptyState />
        ) : (
          <table className="w-full font-mono text-[11px]">
            <thead className="sticky top-0 z-10 bg-surface-1">
              <tr className="border-b border-border-default text-left text-fg-muted">
                <th className="px-3 py-1.5 text-[10px] uppercase tracking-[0.12em]">Name</th>
                <th className="px-3 py-1.5 text-right text-[10px] uppercase tracking-[0.12em]">Value</th>
                <th className="px-3 py-1.5 text-[10px] uppercase tracking-[0.12em]">Type</th>
                <th className="px-3 py-1.5 text-[10px] uppercase tracking-[0.12em]">Group</th>
                <th className="px-3 py-1.5"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  onClick={() => setEditing(p)}
                  className="cursor-pointer border-b border-border-subtle hover:bg-surface-2"
                >
                  <td className="px-3 py-1 text-fg-primary">{p.id}</td>
                  <td className="px-3 py-1 text-right text-fg-primary">
                    <span className="inline-flex items-center gap-1.5">
                      <WriteBadge
                        status={writes[p.id]?.status}
                        onReread={() => refreshOne(p)}
                      />
                      {formatValue(p.value)}
                    </span>
                  </td>
                  <td className="px-3 py-1 text-fg-muted">{mavParamTypeLabel(p.param_type)}</td>
                  <td className="px-3 py-1 text-fg-muted">{paramGroup(p.id)}</td>
                  <td className="px-3 py-1 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        refreshOne(p);
                      }}
                      title="Re-read this param"
                      className="rounded p-0.5 text-fg-muted hover:bg-surface-3 hover:text-fg-primary"
                    >
                      <RefreshCcw className="h-3 w-3" />
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-3 py-4 text-center text-fg-muted">
                    no matches
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <ParamEditDialog
          param={editing}
          robotId={robot.id}
          state={useFleet.getState().states[robot.id]}
          onSaved={(id, value) =>
            setWrites((w) => ({ ...w, [id]: { value, ts: Date.now(), status: 'verifying' } }))
          }
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function GroupPill({ label, count, active, onClick }: { label: string; count: number; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-sm border px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.12em]',
        active
          ? 'border-accent bg-accent-soft/30 text-fg-primary'
          : 'border-border-subtle bg-surface-1 text-fg-muted hover:bg-surface-2 hover:text-fg-secondary',
      )}
    >
      {label} <span className="opacity-60">{count}</span>
    </button>
  );
}

function EmptyState() {
  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="max-w-sm text-center">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-fg-muted">no params loaded</div>
        <p className="mt-2 text-[12px] text-fg-secondary">
          Click <span className="font-medium text-fg-primary">Load all params</span> above to fetch the autopilot's
          parameter table over MAVLink. ArduCopter typically reports ≈1368 entries; expect ~10–30 s on a 57600 baud
          telemetry link.
        </p>
      </div>
    </div>
  );
}

function ParamEditDialog({
  param,
  robotId,
  state,
  onSaved,
  onClose,
}: {
  param: ParamValue;
  robotId: string;
  state?: RobotState;
  onSaved: (paramId: string, value: number) => void;
  onClose: () => void;
}) {
  const meta = paramMeta(param.id);
  const isArmed = state?.arm === 'armed';
  const [draft, setDraft] = useState(String(param.value));
  const [confirmName, setConfirmName] = useState('');
  const parsed = Number(draft);
  const isValidNumber = !Number.isNaN(parsed) && Number.isFinite(parsed);
  const changed = isValidNumber && parsed !== param.value;
  const outOfRange =
    meta && (meta.min !== undefined && parsed < meta.min || meta.max !== undefined && parsed > meta.max);
  const confirmed = confirmName.trim() === param.id;
  const canSave = changed && isValidNumber && !isArmed && confirmed;

  function save() {
    if (!canSave) return;
    sendCommand({
      kind: 'param_set',
      id: `param-set-${Date.now()}`,
      robot_id: robotId,
      priority: 'normal',
      param_id: param.id,
      value: parsed,
      param_type: param.param_type,
    });
    onSaved(param.id, parsed);
    onClose();
  }

  function reboot() {
    sendCommand({
      kind: 'reboot',
      id: `reboot-${Date.now()}`,
      robot_id: robotId,
      priority: 'normal',
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded border border-border-default bg-surface-1 shadow-[var(--shadow-pop)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-border-default px-4 py-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-fg-muted">
            {meta?.category ?? paramGroup(param.id)}
            {meta?.rebootRequired && (
              <span className="ml-2 rounded-sm bg-status-warn/20 px-1.5 py-0.5 text-[9px] text-status-warn">
                REBOOT REQUIRED
              </span>
            )}
          </div>
          <div className="font-mono text-[16px] text-fg-primary">{param.id}</div>
          {meta?.description && (
            <p className="mt-1.5 text-[12px] leading-relaxed text-fg-secondary">{meta.description}</p>
          )}
        </div>

        <div className="space-y-3 px-4 py-3 text-[12px]">
          <div className="grid grid-cols-2 gap-3 font-mono text-[11px]">
            <Kv k="current" v={formatValue(param.value)} />
            <Kv k="type" v={mavParamTypeLabel(param.param_type)} />
            {meta?.units && <Kv k="units" v={meta.units} />}
            {meta?.min !== undefined && <Kv k="min" v={String(meta.min)} />}
            {meta?.max !== undefined && <Kv k="max" v={String(meta.max)} />}
            <Kv k="index" v={`${param.index} / ${param.count}`} />
          </div>

          {meta?.values && (
            <div>
              <Label>Quick values</Label>
              <div className="flex flex-wrap gap-1">
                {meta.values.map((v) => (
                  <button
                    key={v.value}
                    onClick={() => setDraft(String(v.value))}
                    className={cn(
                      'rounded-sm border px-2 py-0.5 font-mono text-[10px]',
                      Number(draft) === v.value
                        ? 'border-accent bg-accent-soft/30 text-fg-primary'
                        : 'border-border-subtle bg-surface-1 text-fg-secondary hover:bg-surface-2',
                    )}
                  >
                    {v.value} · {v.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <Label>New value</Label>
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={String(param.value)}
              className="font-mono"
            />
            {!isValidNumber && <Hint kind="critical">Not a valid number.</Hint>}
            {outOfRange && (
              <Hint kind="warn">
                Out of recommended range ({meta?.min} … {meta?.max}). Autopilot may clamp or reject.
              </Hint>
            )}
            {meta?.rebootRequired && changed && (
              <Hint kind="warn">
                This param takes effect only after autopilot reboot. You can save now and reboot from here.
              </Hint>
            )}
          </div>

          {isArmed ? (
            <div className="rounded border border-status-critical/40 bg-status-critical/10 px-3 py-2 text-[11px] text-status-critical">
              <div className="mb-1 flex items-center gap-1.5 font-medium">
                <AlertTriangle className="h-3 w-3" />
                Refused — vehicle is ARMED
              </div>
              <p className="text-fg-secondary">
                Param writes are blocked while the autopilot is armed (Hub-side guard). Disarm first.
              </p>
            </div>
          ) : (
            changed && (
              <div>
                <Label>Type the param name to confirm</Label>
                <Input
                  value={confirmName}
                  onChange={(e) => setConfirmName(e.target.value)}
                  placeholder={param.id}
                  className="font-mono"
                />
                <Hint kind="default">
                  Typing the full name protects against accidental saves and copy-paste mishaps.
                </Hint>
              </div>
            )
          )}
        </div>

        <div className="flex items-center justify-between gap-2 border-t border-border-default px-4 py-2">
          {meta?.rebootRequired && changed && !isArmed ? (
            <Button size="sm" variant="ghost" onClick={reboot} title="Send reboot after save">
              <RotateCw className="h-3 w-3" />
              Save · then reboot
            </Button>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button
              size="sm"
              onClick={save}
              disabled={!canSave}
              title={
                isArmed
                  ? 'Disarm first'
                  : !changed
                    ? 'Change a value'
                    : !confirmed
                      ? 'Type the param name to confirm'
                      : 'Send PARAM_SET'
              }
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Post-write status chip next to the value (ADR 0014 readback). */
function WriteBadge({
  status,
  onReread,
}: {
  status?: WriteStatus;
  onReread: () => void;
}) {
  if (!status) return null;
  if (status === 'verifying') {
    return <Loader2 className="h-3 w-3 animate-spin text-accent" aria-label="verifying write" />;
  }
  if (status === 'verified') {
    return <CheckCircle2 className="h-3 w-3 text-status-good" aria-label="write verified" />;
  }
  // mismatch (autopilot clamped/rejected the value) or timeout (no echo).
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onReread();
      }}
      title={
        status === 'mismatch'
          ? 'Autopilot stored a DIFFERENT value (clamped or rejected) — click to re-read'
          : 'No PARAM_VALUE echo — write unconfirmed. Click to re-read.'
      }
      className="text-status-warn hover:text-status-critical"
    >
      <AlertTriangle className="h-3 w-3" />
    </button>
  );
}

function Kv({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-fg-muted">{k}</span>
      <span className="text-fg-primary">{v}</span>
    </div>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 font-mono text-[10px] uppercase tracking-[0.12em] text-fg-muted">{children}</div>
  );
}

function Hint({ kind, children }: { kind: 'default' | 'warn' | 'critical'; children: React.ReactNode }) {
  return (
    <p
      className={cn(
        'mt-1 text-[10.5px]',
        kind === 'default' && 'text-fg-muted',
        kind === 'warn' && 'text-status-warn',
        kind === 'critical' && 'text-status-critical',
      )}
    >
      {children}
    </p>
  );
}

function formatValue(v: number): string {
  if (Number.isInteger(v)) return String(v);
  const abs = Math.abs(v);
  if (abs > 0 && (abs >= 1e6 || abs < 1e-3)) return v.toExponential(3);
  return v.toFixed(4).replace(/\.?0+$/, '');
}

/** MAV_PARAM_TYPE values 1..10. */
function mavParamTypeLabel(t: number): string {
  return ({
    1: 'u8',
    2: 'i8',
    3: 'u16',
    4: 'i16',
    5: 'u32',
    6: 'i32',
    7: 'u64',
    8: 'i64',
    9: 'f32',
    10: 'f64',
  } as Record<number, string>)[t] ?? `t${t}`;
}
